import React, { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import CountrySelectBox from "./CountrySelectBox";
import { FieldErrorMessage } from "../ui/Text";
import { handleKeyDown } from "@/utils/helper/input";
import { Validation } from "@/types/form";
import { CountryList } from "@/types/global";
import InfoIcon from "@/assets/InfoIcon";

interface PhoneNumberInputProps {
  label: string;
  field: string;
  disabledCountry?: boolean;
  disabled: boolean;
  validation: Validation;
  placeholder: string;
  countryCodeList: CountryList[];
  defaultCountry?: CountryList & { phoneNo: string };
  newFill?: boolean;
  infoText?: string;
}

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({ label, field, disabled = false, defaultCountry, validation, newFill, infoText = "" }) => {
  const [selectedCountry, setSelectedCountry] = useState<any>(defaultCountry);
  const [focus, setFocus] = useState(false);
  const {
    register,
    setValue,
    trigger,
    getValues,
    formState: { errors },
  } = useFormContext();

  const errorMessage: any = errors[field]?.message;

  const validateNumberLength = useCallback(
    (value: number) => {
      if (isNaN(value)) {
        return "This field is required.";
      }
      const valueLength = value.toString().length;
      if (selectedCountry?.country_name_code === "IND" && valueLength !== 10) {
        return "Enter a valid phone number.";
      }
      if (valueLength < 7 || valueLength > 15) {
        return `Enter a valid phone number.`;
      }
      return null;
    },
    [selectedCountry, label]
  );
  const resetFieldValue = useCallback(() => {
    setValue(field, "", { shouldDirty: true, shouldValidate: false });
  }, []);

  const getRegisterOptions = () => {
    const registerOptions: any = {};
    // If field is disabled
    if (disabled) {
      return registerOptions;
    }

    if (validation?.required) {
      registerOptions.required = `This field is required.`;
    }

    if (selectedCountry?.country_name_code) {
      registerOptions.validate = validateNumberLength;
    }
    return registerOptions;
  };

  const handleCountrySelection = useCallback(
    (option: any) => {
      setSelectedCountry(option);
      if (getValues(field)) {
        setTimeout(() => trigger(field), 100);
      }
    },
    [field]
  );

  useEffect(() => {
    if (!disabled) {
      const countryCode = selectedCountry?.country_phone_code?.split("+")[1];
      setValue("phCountryCode", countryCode);
      setValue("phCid", selectedCountry?.cid);
    }
  }, [selectedCountry, getValues(field)]);

  useEffect(() => {
    if (defaultCountry && !disabled) {
      if (newFill) {
        setValue("phCountryCode", defaultCountry.country_phone_code, { shouldDirty: false });
        setValue("phCid", defaultCountry.cid, { shouldDirty: false });
      } else {
        const value = defaultCountry?.country_phone_code?.split("+")[1];
        setValue("phCountryCode", value, { shouldDirty: false });
        setValue("phCid", defaultCountry.cid, { shouldDirty: false });
      }
    }
  }, [defaultCountry]);

  return (
    <>
      <div
        className={`flex relative items-center w-full p-0.5 group-hover rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:outline-none ${
          errors[field]?.message ? "border-text-error" : null
        } ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${focus ? "!border-text-primary-500" : ""}`}
      >
        <CountrySelectBox
          setSelected={handleCountrySelection}
          selectedText={`${selectedCountry?.iso_name} (${selectedCountry?.country_phone_code})`}
          select={selectedCountry}
          disabled={disabled}
          defaultValue={defaultCountry}
          onChangeCallback={resetFieldValue}
        />
        <input
          type="number"
          id={label + "_text"}
          className={`flex items-center text-text-default disabled:cursor-not-allowed w-full !pl-2 px-3 py-7 text-13 outline-none flex-1 `}
          {...register(field, {
            ...getRegisterOptions(),
            valueAsNumber: true,
          })}
          disabled={disabled}
          defaultValue={defaultCountry?.phoneNo}
          autoComplete="off"
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          onKeyDown={(e) => handleKeyDown(validation, e)}
        />
        {infoText && (
          <div className="relative group hover:cursor-pointer px-2">
            <InfoIcon />
            <div
              className={`absolute -bottom-10 -right-1/2 z-20 px-2 bg-white shadow-2xl rounded-xl border opacity-0 group-hover:opacity-100 group-hover:translate-y-0 translate-y-2 transition-all duration-300 ease-out`}
            >
              <span className="text-10 text-red w-fit whitespace-nowrap">{infoText}</span>
            </div>
          </div>
        )}
      </div>
      {errorMessage && <FieldErrorMessage text={errorMessage} />}
    </>
  );
};

export default PhoneNumberInput;
